import React, { useState, useEffect } from 'react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import LoadingSpinner from '../../components/LoadingSpinner'
import { bankAccountService, CompanyBankAccount, CreateBankAccountData, UpdateBankAccountData } from '../../services/bank-account.service'
import { api } from '../../services/api'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  StarIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import {
  BuildingLibraryIcon,
  CreditCardIcon
} from '@heroicons/react/24/solid'

const MALAYSIAN_BANKS = [
  { name: 'Maybank Berhad', code: 'MAYB', color: '#FFBB00' },
  { name: 'CIMB Bank Berhad', code: 'CIMB', color: '#780000' },
  { name: 'Public Bank Berhad', code: 'PBB', color: '#808080' },
  { name: 'RHB Bank', code: 'RHB', color: '#FB923C' },
  { name: 'Hong Leong Bank', code: 'HLB', color: '#A78BFA' },
  { name: 'AmBank', code: 'AMB', color: '#818CF8' },
]

// Card helper components
const CardHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-6 py-4 border-b border-neutral-200">{children}</div>
)

const CardTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <h2 className={`text-lg font-semibold text-neutral-900 ${className}`}>{children}</h2>
)

const CardContent: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={className}>{children}</div>
)

export default function AccountManagementPage() {
  const [accounts, setAccounts] = useState<CompanyBankAccount[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<CompanyBankAccount | null>(null)
  const [formData, setFormData] = useState({
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    bankCode: '',
    branchCode: '',
    accountType: 'CURRENT' as 'CURRENT' | 'SAVINGS',
    currency: 'MYR',
    isActive: true,
    isPrimary: false,
    minimumAmount: '',
    description: '',
    displayPriority: 0,
    notes: ''
  })

  const fetchAccounts = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await bankAccountService.getAllAccounts()
      setAccounts(data)
    } catch (error: any) {
      console.error('Failed to fetch accounts:', error)
      setError(error.response?.data?.message || 'Failed to fetch bank accounts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAccounts()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Get bank code from bank name
      const selectedBank = MALAYSIAN_BANKS.find(bank => bank.name === formData.bankName)
      const bankCode = selectedBank ? selectedBank.code : formData.bankCode || ''

      const newAccount = await bankAccountService.createAccount({
        bankName: formData.bankName,
        accountHolderName: formData.accountHolderName,
        accountNumber: formData.accountNumber,
        bankCode: bankCode,
        branchCode: formData.branchCode || '',
        accountType: formData.accountType,
        currency: formData.currency,
        isActive: formData.isActive,
        isPrimary: formData.isPrimary,
        description: formData.description,
        displayPriority: formData.displayPriority,
        minimumAmount: formData.minimumAmount ? parseFloat(formData.minimumAmount) : 0,
        notes: formData.notes
      })

      console.log('Bank account created successfully:', newAccount)

      setShowAddModal(false)
      setFormData({
        bankName: '',
        accountHolderName: '',
        accountNumber: '',
        bankCode: '',
        branchCode: '',
        accountType: 'CURRENT' as 'CURRENT' | 'SAVINGS',
        currency: 'MYR',
        isActive: true,
        isPrimary: false,
        minimumAmount: '',
        description: '',
        displayPriority: 0,
        notes: ''
      })
      console.log('Fetching accounts after creation...')
      await fetchAccounts()
      console.log('Accounts fetched successfully')
    } catch (error) {
      console.error('Failed to create account:', error)
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedAccount) return

    try {
      // Get bank code from bank name
      const selectedBank = MALAYSIAN_BANKS.find(bank => bank.name === formData.bankName)
      const bankCode = selectedBank ? selectedBank.code : formData.bankCode || ''

      await bankAccountService.updateAccount(selectedAccount.id, {
        bankName: formData.bankName,
        accountHolderName: formData.accountHolderName,
        accountNumber: formData.accountNumber,
        bankCode: bankCode,
        branchCode: formData.branchCode || '',
        accountType: formData.accountType,
        currency: formData.currency,
        isActive: formData.isActive,
        isPrimary: formData.isPrimary,
        description: formData.description,
        displayPriority: formData.displayPriority,
        minimumAmount: formData.minimumAmount ? parseFloat(formData.minimumAmount) : 0,
        notes: formData.notes
      })

      setShowEditModal(false)
      setSelectedAccount(null)
      setFormData({
        bankName: '',
        accountHolderName: '',
        accountNumber: '',
        bankCode: '',
        branchCode: '',
        accountType: 'CURRENT' as 'CURRENT' | 'SAVINGS',
        currency: 'MYR',
        isActive: true,
        isPrimary: false,
        minimumAmount: '',
        description: '',
        displayPriority: 0,
        notes: ''
      })
      fetchAccounts()
    } catch (error) {
      console.error('Failed to update account:', error)
    }
  }

  const handleToggleActive = async (accountId: string) => {
    try {
      await bankAccountService.toggleAccountStatus(accountId)
      fetchAccounts()
    } catch (error) {
      console.error('Failed to toggle account status:', error)
    }
  }

  const handleSetPrimary = async (accountId: string) => {
    try {
      await bankAccountService.setPrimaryAccount(accountId)
      fetchAccounts()
    } catch (error) {
      console.error('Failed to set primary account:', error)
    }
  }

  const handleDelete = async (accountId: string) => {
    if (!confirm('Are you sure you want to delete this bank account?')) return

    try {
      await bankAccountService.deleteAccount(accountId)
      fetchAccounts()
    } catch (error) {
      console.error('Failed to delete account:', error)
    }
  }

  const getBankInfo = (bankName: string) => {
    const bankInfo = MALAYSIAN_BANKS.find(bank =>
      bank.name.toLowerCase() === bankName?.toLowerCase().trim()
    )
    return bankInfo || MALAYSIAN_BANKS[0]
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Bank Accounts</h1>
            <p className="text-neutral-600 mt-1">
              Manage company bank accounts for payment processing
            </p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Add Account</span>
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center text-red-800">
              <XCircleIcon className="h-5 w-5 mr-2" />
              <span className="text-sm font-medium">Error</span>
            </div>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-neutral-600">Total Accounts</p>
                  <p className="text-2xl font-bold text-neutral-900">{accounts?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-neutral-600">Active</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {accounts?.filter(a => a.isActive).length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <StarIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-neutral-600">Primary</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {accounts?.filter(a => a.isPrimary).length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-red-100 rounded-lg">
                  <XCircleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-neutral-600">Inactive</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {accounts?.filter(a => !a.isActive).length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Accounts List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CurrencyDollarIcon className="h-5 w-5" />
              <span>Bank Accounts</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {!accounts || accounts.length === 0 ? (
              <div className="text-center py-12">
                <BuildingLibraryIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No bank accounts yet</h3>
                <p className="text-neutral-600 mb-4">
                  Add your first bank account to start accepting payments
                </p>
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center space-x-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Add First Account</span>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {accounts
                  .sort((a, b) => {
                    // Primary account first, then by display priority
                    if (a.isPrimary && !b.isPrimary) return -1
                    if (!a.isPrimary && b.isPrimary) return 1
                    return b.displayPriority - a.displayPriority
                  })
                  .map((account) => {
                    const bankInfo = getBankInfo(account.bankName)
                    return (
                      <div
                        key={account.id}
                        className={`border rounded-lg p-4 ${
                          account.isActive ? 'border-neutral-200 bg-white' : 'border-neutral-200 bg-neutral-50 opacity-75'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div
                              className="p-3 rounded-lg"
                              style={{ backgroundColor: bankInfo.color }}
                            >
                              <div className="w-6 h-6 rounded" style={{ backgroundColor: 'white', opacity: 0.3 }}></div>
                            </div>
                            <div>
                              <div className="flex items-center space-x-2 mb-2">
                                <h3 className="font-semibold text-neutral-900">
                                  {account.bankName}
                                </h3>
                                {account.isPrimary && (
                                  <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                                    <StarIcon className="h-3 w-3 mr-1" />
                                    Primary
                                  </Badge>
                                )}
                                {account.isActive ? (
                                  <Badge className="bg-green-100 text-green-800 border-green-200">
                                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-800 border-red-200">
                                    <XCircleIcon className="h-3 w-3 mr-1" />
                                    Inactive
                                  </Badge>
                                )}
                              </div>
                              <p className="text-neutral-900 font-medium mb-1">
                                {account.accountHolderName}
                              </p>
                              <p className="text-neutral-600 font-mono text-sm mb-2">
                                **** **** **** {account.accountNumberLast}
                              </p>
                              {account.minimumAmount && (
                                <p className="text-sm text-neutral-600">
                                  Min. Amount: RM {account.minimumAmount.toLocaleString()}
                                </p>
                              )}
                              {account.description && (
                                <p className="text-sm text-neutral-600 mt-2">
                                  {account.description}
                                </p>
                              )}
                              <p className="text-xs text-neutral-500 mt-2">
                                Priority: {account.displayPriority}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleActive(account.id)}
                              className={account.isActive ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}
                            >
                              {account.isActive ? 'Deactivate' : 'Activate'}
                            </Button>
                            {!account.isPrimary && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSetPrimary(account.id)}
                                className="text-yellow-600 hover:bg-yellow-50"
                              >
                                Set Primary
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedAccount(account)
                                setFormData({
                                  bankName: account.bankName,
                                  accountHolderName: account.accountHolderName,
                                  accountNumber: account.accountNumber?.toString() || '',
                                  bankCode: account.bankCode || '',
                                  branchCode: account.branchCode || '',
                                  accountType: account.accountType || 'CURRENT',
                                  currency: account.currency || 'MYR',
                                  isActive: account.isActive,
                                  isPrimary: account.isPrimary,
                                  minimumAmount: account.minimumAmount?.toString() || '',
                                  description: account.description || '',
                                  displayPriority: account.displayPriority || 0,
                                  notes: account.notes || ''
                                })
                                setShowEditModal(true)
                              }}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(account.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Account Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Add Bank Account</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Bank Name
                </label>
                <select
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Bank</option>
                  {MALAYSIAN_BANKS.map((bank) => (
                    <option key={bank.code} value={bank.name}>
                      {bank.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Account Holder Name
                </label>
                <input
                  type="text"
                  value={formData.accountHolderName}
                  onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 1234567890"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Minimum Amount (RM)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.minimumAmount}
                  onChange={(e) => setFormData({ ...formData, minimumAmount: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows={3}
                  placeholder="Optional description for this account"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  Add Account
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Account Modal */}
      {showEditModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Edit Bank Account</h2>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Bank Name
                </label>
                <select
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  {MALAYSIAN_BANKS.map((bank) => (
                    <option key={bank.code} value={bank.name}>
                      {bank.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Account Holder Name
                </label>
                <input
                  type="text"
                  value={formData.accountHolderName}
                  onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter full account number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Minimum Amount (RM)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.minimumAmount}
                  onChange={(e) => setFormData({ ...formData, minimumAmount: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows={3}
                  placeholder="Optional description for this account"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedAccount(null)
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  Update Account
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}